import { AlertTriangle, AlertCircle, Info, Route } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface Alert {
  type: string;
  severity: 'warning' | 'critical';
  message: string;
  count: number;
  total_amount?: number;
}

interface Props {
  alerts: Alert[];
  loading: boolean;
}

const TYPE_ICONS: Record<string, typeof AlertTriangle> = {
  stuck_shipment: AlertCircle,
  late_shipment: AlertCircle,
  idle_driver: Info,
  pending_settlement: Info,
  overdue_settlement: AlertTriangle,
  unassigned_urgent: AlertCircle,
  route_without_driver: Route,
};

export function AlertsWidget({ alerts, loading }: Props) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-foreground/80">التنبيهات التشغيلية</CardTitle>
          {!loading && alerts.length > 0 && (
            <Badge variant="destructive" className="text-[10px]">
              {alerts.length} تنبيه
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 gap-2 text-muted-foreground">
            <span className="text-2xl">✅</span>
            <span className="text-sm">لا توجد تنبيهات</span>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map((alert, i) => {
              const Icon = TYPE_ICONS[alert.type] ?? AlertTriangle;
              const isCritical = alert.severity === 'critical';
              return (
                <div
                  key={i}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border',
                    isCritical
                      ? 'bg-destructive/8 border-destructive/20 text-destructive'
                      : 'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-950/20 dark:border-orange-900/30 dark:text-orange-400',
                  )}
                >
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-snug">{alert.message}</p>
                    {alert.total_amount !== undefined && alert.total_amount > 0 && (
                      <p className="text-[11px] mt-0.5 opacity-75">
                        الإجمالي: {alert.total_amount.toLocaleString('en-US')}
                      </p>
                    )}
                  </div>
                  <Badge
                    variant={isCritical ? 'destructive' : 'outline'}
                    className="text-[9px] shrink-0"
                  >
                    {isCritical ? 'حرج' : 'تحذير'}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
